/**
 * StudioLinks – inline CRUD for creator_links.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Trash2, Plus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const PLATFORM_OPTIONS = [
  "Instagram", "TikTok", "Snapchat", "X / Twitter", "YouTube",
  "OnlyFans", "Fansly", "LoyalFans", "ManyVids", "Pornhub", "Other",
];

export default function StudioLinks() {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // Bruk dedikert /me-endepunkt — det offentlige /users/{u}/creator-page
      // kan returnere {paused: true} eller treffe annen bruker via creator_handle-slug.
      const r = await api.get("/me/creator-page");
      setLinks(r.data?.creator_links || []);
    } catch {
      setLinks([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/me/creator-page", { creator_links: next });
      // Refetch fra server så ID-er/posisjoner stemmer med DB
      await load();
      toast.success(t("studio.links.updated_ok"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
                  : Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
                  : t("studio.links.save_err");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const add = () => {
    if (!newLabel.trim() || !newUrl.trim()) {
      toast.error(t("studio.links.both_required"));
      return;
    }
    if (!/^https?:\/\//i.test(newUrl.trim())) {
      toast.error(t("studio.links.url_must_start"));
      return;
    }
    const next = [...links, {
      platform: newLabel.trim(),
      label: newLabel.trim(),
      url: newUrl.trim(),
      type: "url",
    }];
    save(next);
    setNewLabel(""); setNewUrl("");
  };

  const remove = (idx) => {
    if (!window.confirm(t("studio.links.delete_confirm"))) return;
    save(links.filter((_, i) => i !== idx));
  };

  const updateField = (idx, field, value) => {
    // Hold platform i synk med label så backend godtar PATCH (platform er påkrevd).
    const next = links.map((l, i) => {
      if (i !== idx) return l;
      const patch = { [field]: value };
      if (field === "label") patch.platform = value || l.platform || "Link";
      return { ...l, ...patch };
    });
    setLinks(next);
  };
  const commit = () => {
    // Sørg for at hver lenke har platform satt før vi sender til backend
    const cleaned = links.map((l) => ({
      ...l,
      platform: (l.platform || l.label || "Link").toString().trim().slice(0, 40),
      label: (l.label || l.platform || "").toString().trim(),
      url: (l.url || "").trim(),
      type: l.type || "url",
    })).filter((l) => l.url || l.qr_image_url);
    save(cleaned);
  };

  return (
    <section data-testid="studio-links">
      <div className="panel-tight p-5 mb-6">
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{t("studio.links.title")}</h2>
        <p className="text-sm text-[var(--cream-soft)] mb-4">{t("studio.links.lead")}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("studio.links.platform_label")}
            </label>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} maxLength={40}
              placeholder="Instagram" list="platform-options"
              className="input-editorial w-full" data-testid="link-new-label" />
            <datalist id="platform-options">
              {PLATFORM_OPTIONS.map((p) => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div className="md:col-span-2">
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("studio.links.url_label")}</label>
            <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} maxLength={500}
              placeholder="https://..." className="input-editorial w-full" data-testid="link-new-url" />
          </div>
        </div>
        <button onClick={add} disabled={saving} className="btn-magenta disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="link-add">
          <Plus className="h-3.5 w-3.5" /> {t("studio.links.add_btn")}
        </button>
      </div>

      <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">{t("studio.links.your_links")}</h3>
      {loading && <div className="text-sm text-[var(--cream-soft)]">{t("common.loading")}</div>}
      {!loading && links.length === 0 && (
        <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">
          {t("studio.links.empty")}
        </div>
      )}
      {!loading && links.length > 0 && (
        <ul className="space-y-2">
          {links.map((l, idx) => (
            <li key={idx} className="panel-tight p-3 flex items-center gap-3" data-testid={`link-row-${idx}`}>
              <GripVertical className="h-4 w-4 text-[var(--cream-faint)] shrink-0" />
              <input value={l.label || ""} onChange={(e) => updateField(idx, "label", e.target.value)}
                onBlur={commit} maxLength={40}
                className="input-editorial w-40 text-sm" data-testid={`link-label-${idx}`} />
              <input value={l.url || ""} onChange={(e) => updateField(idx, "url", e.target.value)}
                onBlur={commit} maxLength={500}
                className="input-editorial flex-1 text-sm" data-testid={`link-url-${idx}`} />
              <a href={l.url} target="_blank" rel="noopener noreferrer"
                className="text-[var(--cream-faint)] hover:text-[var(--magenta)]" title={t("studio.links.open")}>
                <ExternalLink className="h-4 w-4" />
              </a>
              <button onClick={() => remove(idx)}
                className="text-red-300 hover:text-red-200" data-testid={`link-remove-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
